import React, { createContext, useState, useContext, useEffect } from 'react';
import authFetch from '../utils/authFetch';

const DocumentationContext = createContext();

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const useDocumentation = () => {
  const context = useContext(DocumentationContext);
  if (!context) {
    throw new Error('useDocumentation must be used within a DocumentationProvider');
  }
  return context;
};

export const DocumentationProvider = ({ children }) => {
  const [cards, setCards] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize and fetch cards on mount
  useEffect(() => {
    const initializeDocs = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, initialize the table if needed
        try {
          await authFetch(`${API_BASE}/api/docs/init`, { method: 'POST' });
        } catch (e) {
          console.warn('Table init failed (may already exist):', e);
        }

        // Fetch all cards
        const response = await authFetch(`${API_BASE}/api/docs/cards`);
        if (!response.ok) throw new Error('Failed to fetch cards');
        
        const data = await response.json();
        const cardsObj = {};
        
        for (const card of data.cards) {
          const cardDetail = await authFetch(`${API_BASE}/api/docs/cards/${card.card_id}`);
          if (cardDetail.ok) {
            const detail = await cardDetail.json();
            cardsObj[card.card_id] = {
              id: card.card_id,
              name: card.card_name,
              folders: detail.folders_data
            };
          }
        }
        
        setCards(cardsObj);
      } catch (err) {
        console.error('Documentation init error:', err);
        setError(err.message);
        // Fallback to empty state
        setCards({});
      } finally {
        setLoading(false);
      }
    };

    initializeDocs();
  }, []);

  const addCard = async (name) => {
    try {
      const id = name.toLowerCase().replace(/\s+/g, '-');
      
      const response = await authFetch(`${API_BASE}/api/docs/cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardId: id, cardName: name })
      });
      
      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('Card already exists');
        }
        throw new Error('Failed to create card');
      }
      
      const newCard = await response.json();
      setCards({
        ...cards,
        [id]: {
          id,
          name,
          folders: { root: { type: 'folder', children: {} } }
        }
      });
      return id;
    } catch (err) {
      console.error('Add card error:', err);
      setError(err.message);
      throw err;
    }
  };

  const deleteCard = async (id) => {
    try {
      const response = await authFetch(`${API_BASE}/api/docs/cards/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete card');
      
      const newCards = { ...cards };
      delete newCards[id];
      setCards(newCards);
    } catch (err) {
      console.error('Delete card error:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateCardFolders = async (cardId, folders) => {
    try {
      const response = await authFetch(`${API_BASE}/api/docs/cards/${cardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folders })
      });
      
      if (!response.ok) throw new Error('Failed to update card');
      
      setCards({
        ...cards,
        [cardId]: {
          ...cards[cardId],
          folders
        }
      });
    } catch (err) {
      console.error('Update card error:', err);
      setError(err.message);
      throw err;
    }
  };

  return (
    <DocumentationContext.Provider value={{ cards, loading, error, addCard, deleteCard, updateCardFolders }}>
      {children}
    </DocumentationContext.Provider>
  );
};
